/**
 * Chores + allowance. One page, two very different jobs:
 *
 *   Adults — manage the chore list and its rates, approve what got done, pay out.
 *   Kids   — see their chores, hit "I did it", watch their money add up.
 *
 * The split is keyed off the verified identity. The frontend split is a courtesy;
 * worker/routes/chores.ts is what actually enforces it (this router is not behind
 * the adult gate, so it checks role on every adult action itself).
 *
 * Two things this page must never do, both of which used to lose a kid money:
 *
 *   1. Derive the approvals queue by pulling a capped page of recent completions
 *      and filtering in the browser. Once newer completions push an unapproved one
 *      out of the window it vanishes from "Needs approval" forever and never gets
 *      paid. The queue comes from `?status=pending`, which is unbounded by recency.
 *   2. Decide in the browser's timezone whether a chore is "already done today".
 *      The house has one timezone and the Worker holds it: `?scope=current` returns
 *      exactly the completions that still count for their chore's cadence.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type {
  AllowanceBalance,
  AllowanceEntry,
  Chore,
  ChoreCompletion,
  MemberSummary,
} from '../../shared/types';
import { ErrorState, LoadingState } from './shared';
import AdultView from './AdultView';
import KidView from './KidView';

export interface AdultData {
  chores: Chore[];
  /** Every unapproved completion in the house. Never a filtered page. */
  pending: ChoreCompletion[];
  /** The last few approved ones, for the "recently approved" strip. */
  recent: ChoreCompletion[];
  members: MemberSummary[];
  balances: AllowanceBalance[];
}

export interface KidData {
  chores: Chore[];
  /** Completions still inside their chore's current cadence window, per the Worker. */
  current: ChoreCompletion[];
  /** Everything they checked off this household week — the "you're on a roll" count. */
  thisWeek: ChoreCompletion[];
  me: MemberSummary | null;
  balance: AllowanceBalance;
  history: AllowanceEntry[];
}

const EMPTY_BALANCE = (memberId: number | null): AllowanceBalance => ({
  member_id: memberId ?? 0,
  balance: 0,
  pending: 0,
});

export default function ChoresPage() {
  const identity = useIdentity();
  const isAdult = identity.role === 'adult';

  const [adult, setAdult] = useState<AdultData | null>(null);
  const [kid, setKid] = useState<KidData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** A kid's own card comes straight off the verified identity — colour included. */
  const me = useMemo<MemberSummary | null>(
    () =>
      identity.memberId == null
        ? null
        : {
            id: identity.memberId,
            name: identity.name ?? 'Me',
            color: identity.color ?? 'var(--color-sage)',
            role: identity.role,
          },
    [identity.memberId, identity.name, identity.color, identity.role]
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      if (isAdult) {
        const [chores, pending, recent, members, balances] = await Promise.all([
          api.get<Chore[]>('/chores'),
          api.get<ChoreCompletion[]>('/chores/completions?status=pending&limit=300'),
          api.get<ChoreCompletion[]>('/chores/completions?status=approved&limit=8'),
          api.get<MemberSummary[]>('/chores/members'),
          api.get<AllowanceBalance[]>('/chores/allowance/balances'),
        ]);
        setAdult({ chores, pending, recent, members, balances });
      } else {
        const memberId = identity.memberId;
        const [chores, current, thisWeek, balance, history] = await Promise.all([
          api.get<Chore[]>('/chores'),
          api.get<ChoreCompletion[]>('/chores/completions?scope=current'),
          api.get<ChoreCompletion[]>('/chores/completions?scope=week'),
          memberId == null
            ? Promise.resolve(EMPTY_BALANCE(memberId))
            : api.get<AllowanceBalance>('/chores/allowance'),
          memberId == null
            ? Promise.resolve([] as AllowanceEntry[])
            : api.get<AllowanceEntry[]>('/chores/allowance/history'),
        ]);
        setKid({ chores, current, thisWeek, me, balance, history });
      }
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'We could not reach the house. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [isAdult, identity.memberId, me]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Header name={identity.name} isAdult={isAdult} />
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Header name={identity.name} isAdult={isAdult} />
        <ErrorState
          message={error}
          onRetry={() => {
            setLoading(true);
            void load();
          }}
        />
      </div>
    );
  }

  if (isAdult && adult) return <AdultView data={adult} reload={load} />;
  if (!isAdult && kid) return <KidView data={kid} reload={load} />;

  return (
    <ErrorState
      message="We could not load your chores."
      onRetry={() => {
        setLoading(true);
        void load();
      }}
    />
  );
}

function Header({ name, isAdult }: { name: string | null; isAdult: boolean }) {
  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Chores</h1>
      <p className="mt-1 text-sm text-ink-soft">
        {isAdult ? 'Approvals, rates, and allowance.' : `Let's get after it, ${name ?? 'friend'}.`}
      </p>
    </div>
  );
}
